/**
 * S14 — Governance Hub · Policy Catalogue (`/govern/policies`).
 *
 * Single source of truth for every ALGOS-enforced policy. Each policy
 * is a row that can be opened in the right drawer to inspect its intent,
 * rules, scope, linked agents, version log and recent audit excerpt.
 *
 * Authoring / designer flows are deferred to a later wave.
 */
import React, { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  ShieldCheck,
  PenLine,
  AlertTriangle,
  Activity,
  RefreshCw,
  Plus,
  ArrowUpRight,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useTenantData } from "@/contexts/TenantModeContext";
import { useEntityList } from "@/lib/useEntity";
import { SyncedIndicator } from "@/components/data/SyncedIndicator";
import { Flywheel } from "@/components/brand/Flywheel";
import { KpiTile } from "@/pages/dashboard/components/DashboardPrimitives";
import { cn } from "@/lib/utils";
import { PolicyStatusPill, ViolationsBadge, SeverityDot } from "./components/PolicyPrimitives";
import PolicyDetailDrawer from "./PolicyDetailDrawer";

const CATEGORY_TABS = [
  { id: "all", label: "All" },
  { id: "Privacy", label: "Privacy" },
  { id: "Risk", label: "Risk" },
  { id: "Vendor", label: "Vendor" },
  { id: "Financial", label: "Financial" },
  { id: "Quality", label: "Quality" },
];

const STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "active", label: "Active" },
  { value: "draft", label: "Draft" },
];

const VIOLATIONS_OPTIONS = [
  { value: "", label: "Any violations" },
  { value: "zero", label: "Zero (clean)" },
  { value: "any", label: "1 or more" },
];

const SORTS = [
  { id: "violations-desc", label: "Violations · high → low", cmp: (a, b) => b.violations - a.violations },
  { id: "scope-desc", label: "Scope · widest → narrowest", cmp: (a, b) => b.appliesTo - a.appliesTo },
  { id: "updated-desc", label: "Updated · newest", cmp: (a, b) => (b.lastUpdated || "").localeCompare(a.lastUpdated || "") },
  { id: "name-asc", label: "Name · A → Z", cmp: (a, b) => a.name.localeCompare(b.name) },
];

export default function PolicyCatalogue() {
  const { user } = useAuth();
  // Phase 3b — primary list comes from the REST endpoint; cross-entity
  // slices (agents/auditEntries used for drift counts + audit trail
  // subtabs) stay on the bundle for now.
  const { items: policies, loading, error, lastSyncedAt, refetch } = useEntityList("/api/policies");
  const { agents, auditEntries, isCustomer } = useTenantData();
  const navigate = useNavigate();

  const [category, setCategory] = useState("all");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [violations, setViolations] = useState("");
  const [ownerFilter, setOwnerFilter] = useState("");
  const [sortId, setSortId] = useState("violations-desc");
  const [selected, setSelected] = useState(null);

  // Derive the live agent-count per policy from the agent inventory.
  // This is the authoritative count — overrides the seeded `appliesTo`.
  const policiesWithCounts = useMemo(() => {
    const counts = new Map();
    agents.forEach((a) => {
      (a.policyIds || []).forEach((pid) => counts.set(pid, (counts.get(pid) || 0) + 1));
    });
    return policies.map((p) => ({
      ...p,
      liveAgentCount: counts.get(p.id) ?? p.appliesTo,
    }));
  }, [policies, agents]);

  const owners = useMemo(
    () => Array.from(new Set(policies.map((p) => p.owner))).sort(),
    [policies],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const sortCmp = SORTS.find((s) => s.id === sortId)?.cmp || SORTS[0].cmp;
    return policiesWithCounts
      .filter((p) => {
        if (category !== "all" && p.category !== category) return false;
        if (status && p.status !== status) return false;
        if (ownerFilter && p.owner !== ownerFilter) return false;
        if (violations === "zero" && p.violations !== 0) return false;
        if (violations === "any" && p.violations === 0) return false;
        if (q && !`${p.id} ${p.name} ${p.intent || ""} ${p.owner} ${p.category}`.toLowerCase().includes(q)) return false;
        return true;
      })
      .sort(sortCmp);
  }, [policiesWithCounts, category, status, ownerFilter, violations, search, sortId]);

  const resetFilters = useCallback(() => {
    setCategory("all");
    setSearch("");
    setStatus("");
    setViolations("");
    setOwnerFilter("");
    setSortId("violations-desc");
  }, []);

  const closeDrawer = useCallback(() => setSelected(null), []);
  const openPolicy = useCallback((p) => setSelected(p), []);
  // Always pull the latest enriched record so the drawer reflects filter mutations.
  const livePolicy = selected
    ? policiesWithCounts.find((p) => p.id === selected.id) || selected
    : null;

  if (loading && policies.length === 0) return <PolicySkeleton />;
  if (error) return <PolicyError message={error.message} />;
  if (isCustomer && policies.length === 0) {
    return <CustomerEmpty userName={user?.name?.split(" ")[0]} onSubmit={() => navigate("/govern/agents")} />;
  }

  return (
    <div className="relative px-6 lg:px-8 py-6 anim-fade-up" data-testid="policy-catalogue">
      <div className="absolute -right-24 -top-12 pointer-events-none">
        <Flywheel size={300} opacity={0.05} />
      </div>

      <Header lastSyncedAt={lastSyncedAt} syncing={loading} onRefetch={refetch} />
      <KpiStrip policies={policiesWithCounts} />
      <CategoryTabs current={category} setCurrent={setCategory} policies={policiesWithCounts} />

      <div className="mt-4 surface-card shadow-e1 overflow-hidden" data-testid="policy-panel">
        <Toolbar
          search={search}
          setSearch={setSearch}
          status={status}
          setStatus={setStatus}
          violations={violations}
          setViolations={setViolations}
          owners={owners}
          ownerFilter={ownerFilter}
          setOwnerFilter={setOwnerFilter}
          sortId={sortId}
          setSortId={setSortId}
          matchCount={filtered.length}
          totalCount={policiesWithCounts.length}
          onReset={resetFilters}
        />
        <PolicyTable items={filtered} onSelect={openPolicy} />
      </div>

      <PolicyDetailDrawer
        policy={livePolicy}
        onClose={closeDrawer}
        agents={agents}
        auditEntries={auditEntries}
      />
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
function Header({ lastSyncedAt, syncing, onRefetch }) {
  return (
    <div className="relative flex items-start justify-between gap-4 flex-wrap">
      <div>
        <p className="eyebrow">Govern · Policy Catalogue</p>
        <h1 className="text-display-md text-ink-primary mt-1">Policies</h1>
        <p className="text-ink-secondary mt-1 max-w-2xl">
          Every governance rule ALGOS enforces across the AI fleet —
          with live scope, violation counts and the regulatory frameworks
          each rule maps to. Open any policy to inspect intent, rules and
          version history.
        </p>
      </div>
      <SyncedIndicator
        lastSyncedAt={lastSyncedAt}
        loading={syncing}
        onRefetch={onRefetch}
        testId="policy-catalogue-synced"
      />
    </div>
  );
}

function PolicySkeleton() {
  return (
    <div className="px-6 lg:px-8 py-6 anim-fade-up" data-testid="policy-catalogue-loading">
      <div className="h-8 w-72 rounded-md bg-inset/60 animate-pulse" />
      <div className="mt-2 h-4 w-96 rounded-md bg-inset/40 animate-pulse" />
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-24 rounded-md surface-card animate-pulse bg-inset/30" />
        ))}
      </div>
      <div className="mt-6 h-[420px] rounded-md surface-card animate-pulse bg-inset/20" />
    </div>
  );
}

function PolicyError({ message }) {
  return (
    <div className="px-6 lg:px-8 py-6" data-testid="policy-catalogue-error">
      <div className="surface-card shadow-e1 p-5 border-l-[3px] border-l-risk-high">
        <p className="eyebrow text-risk-high">Govern · Policy Catalogue</p>
        <h1 className="text-h2 text-ink-primary mt-1">Couldn&apos;t load policies</h1>
        <p className="mt-2 text-meta text-ink-secondary">{message || "Network error — please retry."}</p>
      </div>
    </div>
  );
}

function KpiStrip({ policies }) {
  const active = policies.filter((p) => p.status === "active").length;
  const draft = policies.filter((p) => p.status === "draft").length;
  const violations = policies.reduce((s, p) => s + (p.violations || 0), 0);
  const drift = policies.filter((p) => p.category === "Quality").length;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
      <KpiTile
        label="Active policies"
        value={active.toString()}
        delta={`${draft} in draft`}
        deltaTone={draft > 0 ? "neutral" : "up"}
        hint="enforced across the AI fleet"
        icon={ShieldCheck}
        tone="tralenex"
        testId="policy-kpi-active"
      />
      <KpiTile
        label="Policies in draft"
        value={draft.toString()}
        delta="Awaiting sign-off"
        deltaTone="neutral"
        hint="not yet enforced"
        icon={PenLine}
        tone="gold"
        testId="policy-kpi-draft"
      />
      <KpiTile
        label="Total violations"
        value={violations.toString()}
        delta={violations > 0 ? "Action required" : "All clean"}
        deltaTone={violations > 0 ? "down" : "up"}
        hint="across all enforced policies"
        icon={AlertTriangle}
        tone="risk"
        testId="policy-kpi-violations"
      />
      <KpiTile
        label="Drift policies"
        value={drift.toString()}
        delta="Quality-control rules"
        deltaTone="up"
        hint="performance / drift category"
        icon={Activity}
        tone="opp"
        testId="policy-kpi-drift"
      />
    </div>
  );
}

function CategoryTabs({ current, setCurrent, policies }) {
  return (
    <div className="mt-6 flex items-center gap-1 border-b border-border-subtle flex-wrap" data-testid="policy-tabs">
      {CATEGORY_TABS.map((t) => {
        const count = t.id === "all" ? policies.length : policies.filter((p) => p.category === t.id).length;
        const active = current === t.id;
        return (
          <button
            key={t.id}
            type="button"
            onClick={() => setCurrent(t.id)}
            aria-pressed={active}
            data-testid={`policy-tab-${t.id}`}
            className={cn(
              "inline-flex items-center gap-2 px-4 h-10 border-b-2 -mb-px transition-colors",
              active
                ? "border-tralenex-600 text-ink-primary"
                : "border-transparent text-ink-secondary hover:text-ink-primary",
            )}
          >
            <span className="text-sm font-semibold">{t.label}</span>
            <span
              className={cn(
                "inline-flex items-center justify-center min-w-[20px] h-[20px] rounded px-1.5 text-[10px] font-bold tabular-nums",
                active ? "bg-tralenex-600 text-white" : "bg-inset text-ink-secondary",
              )}
            >
              {count}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function Toolbar(props) {
  const {
    search, setSearch,
    status, setStatus,
    violations, setViolations,
    owners, ownerFilter, setOwnerFilter,
    sortId, setSortId,
    matchCount, totalCount,
    onReset,
  } = props;
  return (
    <div className="px-4 pt-3 pb-3 border-b border-border-subtle flex flex-wrap items-center gap-2" data-testid="policy-toolbar">
      <div className="relative flex-1 min-w-[240px]">
        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-disabled" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search policies, owners, intent…"
          className="w-full pl-8 pr-3 h-8 text-sm rounded-md border border-border-subtle bg-white focus:outline-none focus:border-tralenex-400"
          data-testid="policy-search-input"
        />
      </div>
      <Select value={status} onChange={setStatus} testId="policy-filter-status">
        {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </Select>
      <Select value={violations} onChange={setViolations} testId="policy-filter-violations">
        {VIOLATIONS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </Select>
      <Select value={ownerFilter} onChange={setOwnerFilter} testId="policy-filter-owner">
        <option value="">All owners</option>
        {owners.map((o) => <option key={o} value={o}>{o}</option>)}
      </Select>
      <Select value={sortId} onChange={setSortId} testId="policy-sort-select">
        {SORTS.map((s) => <option key={s.id} value={s.id}>{`Sort · ${s.label}`}</option>)}
      </Select>
      <button
        type="button"
        onClick={onReset}
        className="inline-flex items-center gap-1 h-8 px-2 rounded-md text-meta text-ink-secondary hover:bg-inset"
        data-testid="policy-reset-filters"
      >
        <RefreshCw size={11} /> Reset
      </button>
      <span className="flex-1" />
      <span className="text-meta text-ink-secondary tabular-nums" data-testid="policy-match-count">
        {matchCount} / {totalCount}
      </span>
    </div>
  );
}

function Select({ value, onChange, testId, children }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
      className="h-8 px-2 text-sm rounded-md border border-border-subtle bg-white focus:outline-none focus:border-tralenex-400"
    >
      {children}
    </select>
  );
}

function PolicyTable({ items, onSelect }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-inset/40 border-b border-border-subtle">
          <tr>
            {["Policy", "Category", "Owner", "Status", "Agents in scope", "Violations", "Updated", ""].map((h) => (
              <th key={h} className="px-3 py-2 text-left text-meta font-semibold tracking-[0.04em] uppercase text-ink-secondary">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-border-subtle">
          {items.map((p) => (
            <tr
              key={p.id}
              onClick={() => onSelect(p)}
              className="hover:bg-inset/40 cursor-pointer transition-colors"
              data-testid={`policy-row-${p.id}`}
            >
              <td className="px-3 py-2.5">
                <div className="flex items-start gap-2.5">
                  <SeverityDot severity={p.severity} />
                  <div className="min-w-0">
                    <p className="font-medium text-ink-primary truncate max-w-[320px]">{p.name}</p>
                    <p className="text-meta text-ink-secondary font-mono tabular-nums">{p.id}</p>
                  </div>
                </div>
              </td>
              <td className="px-3 py-2.5 text-ink-secondary">{p.category}</td>
              <td className="px-3 py-2.5 text-ink-primary">{p.owner}</td>
              <td className="px-3 py-2.5"><PolicyStatusPill status={p.status} /></td>
              <td className="px-3 py-2.5">
                <span className="inline-flex items-center gap-1 text-meta tabular-nums">
                  <span className="font-semibold text-ink-primary">{p.liveAgentCount}</span>
                  <span className="text-ink-disabled">agents</span>
                </span>
              </td>
              <td className="px-3 py-2.5">
                <ViolationsBadge count={p.violations} />
              </td>
              <td className="px-3 py-2.5 text-meta text-ink-disabled tabular-nums">{p.lastUpdated}</td>
              <td className="px-3 py-2.5 text-ink-disabled"><ArrowUpRight size={14} /></td>
            </tr>
          ))}
          {items.length === 0 && (
            <tr>
              <td colSpan={8} className="px-6 py-10 text-center text-ink-secondary">
                No policies match the current filters.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function CustomerEmpty({ userName, onSubmit }) {
  return (
    <div className="relative min-h-[calc(100vh-112px)] px-6 lg:px-10 py-10 overflow-hidden anim-fade-up" data-testid="policy-customer-empty">
      <div className="absolute -right-40 -bottom-40 pointer-events-none">
        <Flywheel size={620} opacity={0.04} />
      </div>
      <div className="relative max-w-3xl">
        <p className="eyebrow">Govern · Policy Catalogue</p>
        <h1 className="text-display-lg text-ink-primary mt-1">
          {userName ? `Welcome, ${userName}.` : "Welcome."}
        </h1>
        <p className="text-ink-secondary mt-3 max-w-xl text-[15px]">
          No policies yet. ALGOS ships with a baseline policy library
          (residency, PII, vendor allow-list, autonomy caps, cost caps,
          drift thresholds) — connect your agents in the Inventory and
          we will surface the right baseline for your tenant.
        </p>
        <button
          type="button"
          onClick={onSubmit}
          className="mt-6 inline-flex items-center gap-2 h-10 px-4 rounded-md bg-tralenex-600 text-white text-sm font-semibold hover:bg-tralenex-700"
          data-testid="policy-empty-cta"
        >
          <Plus size={14} /> Set up agent inventory
        </button>
      </div>
    </div>
  );
}
